import type { MacCpuArch, WindowsCpuArch } from "@/lib/platform";

type NavigatorWithUAData = Navigator & {
  userAgentData?: {
    getHighEntropyValues?: (
      hints: string[],
    ) => Promise<{ architecture?: string; bitness?: string }>;
  };
};

export function parseWindowsArchFromUserAgent(ua: string): WindowsCpuArch {
  if (!/\bWindows\b/i.test(ua)) return "unknown";

  const windowsParen = [...ua.matchAll(/\(([^)]*)\)/g)].find((m) =>
    /\bWindows\b/i.test(m[1] ?? ""),
  );
  const inner = windowsParen?.[1] ?? "";

  if (inner) {
    if (/\bWin64;\s*ARM64\b/i.test(inner) || /;\s*ARM64\b/i.test(inner))
      return "arm64";
    if (/\bWin64;\s*x64\b/i.test(inner)) return "x64";
    if (/\bWOW64\b/i.test(inner)) return "x64";
  }

  if (/\bWin64;\s*ARM64\b/i.test(ua)) return "arm64";
  if (/\bWin64;\s*x64\b/i.test(ua)) return "x64";

  return "unknown";
}

export async function detectWindowsCpuArch(): Promise<WindowsCpuArch> {
  const ua = navigator.userAgent;
  if (!/\bWindows\b/i.test(ua)) return "unknown";

  const uaData = (navigator as NavigatorWithUAData).userAgentData;
  if (uaData?.getHighEntropyValues) {
    try {
      const hints = await uaData.getHighEntropyValues([
        "architecture",
        "bitness",
      ]);
      if (hints.architecture === "arm") return "arm64";
      if (hints.architecture === "x86" && hints.bitness === "64") return "x64";
      if (
        hints.architecture === "x86" &&
        hints.bitness === "32" &&
        /\bWOW64\b/i.test(ua)
      )
        return "x64";
    } catch {
      /* 回退到 UA */
    }
  }

  return parseWindowsArchFromUserAgent(ua);
}

export function parseMacArchFromUserAgent(ua: string): MacCpuArch {
  if (!/\bMac OS X\b|\bMacintosh\b/i.test(ua)) return "unknown";
  if (/\bIntel Mac OS X\b/i.test(ua)) return "x64";
  if (/\barm64\b|\baarch64\b/i.test(ua)) return "arm64";
  return "unknown";
}

export async function detectMacCpuArch(): Promise<MacCpuArch> {
  const ua = navigator.userAgent;
  if (!/\bMac OS X\b|\bMacintosh\b/i.test(ua)) return "unknown";

  const uaData = (navigator as NavigatorWithUAData).userAgentData;
  if (uaData?.getHighEntropyValues) {
    try {
      const hints = await uaData.getHighEntropyValues([
        "architecture",
        "bitness",
      ]);
      if (hints.architecture === "arm") return "arm64";
      if (hints.architecture === "x86" && hints.bitness === "64") return "x64";
    } catch {
      /* 回退到 UA */
    }
  }

  return parseMacArchFromUserAgent(ua);
}
